<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <div ref="root" class="attr-fv"
            v-wjTooltip="attr.tooltip"
            v-wjContextMenu="'ContextWidgetMenuId'"
        >
            <label class="label-attr" v-if="attr.title && !attr.hideTitle" :for="attr.html_id">{{ attr.title }}</label>

            <!-- IE 2026 09 23 / ODOO-341407: i18n -->
            <div v-if="src && (fileTitle || viewerType !== 'file')" class="attr-fv-header">
                <i :class="['pi', fileIcon, 'attr-fv-header-icon']" />
                <span class="attr-fv-header-title">{{ fileTitle || resolvedFileType }}</span>
                <button class="attr-fv-btn" @click="download" :title="t('Download')">
                    <i class="pi pi-download" />
                </button>
                <button v-if="viewerType !== 'audio' && viewerType !== 'video'" class="attr-fv-btn" @click="openFull" :title="t('Open in new tab')">
                    <i class="pi pi-external-link" />
                </button>
            </div>

            <div ref="wrap" class="attr-fv-wrap" :style="wrapStyle">
                <div v-if="!src" class="attr-fv-empty">
                    <i class="pi pi-file" />
                </div>

                <img v-else-if="viewerType === 'image'"
                    :src="src"
                    class="attr-fv-img"
                    :style="{ objectFit: attr.objectFit || 'contain' }"
                    @click="openFull"
                />

                <iframe v-else-if="viewerType === 'pdf'"
                    :src="pdfSrc"
                    class="attr-fv-frame"
                />

                <div v-else-if="viewerType === 'excel'" class="attr-fv-excel">
                    <div v-if="excelError" class="attr-fv-excel-error">{{ excelError }}</div>
                    <wj-flex-sheet v-show="!excelError" class="attr-fv-flex-sheet" :initialized="initFlexSheet" />
                </div>

                <video v-else-if="viewerType === 'video'"
                    :src="src" class="attr-fv-video" controls
                />

                <audio v-else-if="viewerType === 'audio'"
                    :src="src" class="attr-fv-audio" controls
                />

                <pre v-else-if="viewerType === 'text'" class="attr-fv-text">{{ textContent }}</pre>

                <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                <div v-else class="attr-fv-card">
                    <i :class="['pi', fileIcon, 'attr-fv-card-icon']" />
                    <div class="attr-fv-card-body">
                        <div class="attr-fv-card-title">{{ fileTitle || t('File') }}</div>
                        <div class="attr-fv-card-mime">{{ resolvedFileType }}</div>
                    </div>
                    <button class="attr-fv-dl" @click="download" :title="t('Download')">
                        <i class="pi pi-download" />
                    </button>
                </div>
            </div>
        </div>
    </Attr>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'
import { t } from '@/i18n'   // IE 2026 09 23 / ODOO-341407: i18n

const EXCEL_MIMES = new Set([
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
])

export default {
    props: ['widget_id', 'attr_id'],
    data: () => ({
        WIDGET: {},
        src: '',
        fileTitle: '',
        resolvedFileType: '',
        blobUrl: null,
        excelError: '',
        flexSheet: null,
    }),
    setup() {
        const store = useMainStore()
        return { store }
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        this.pendingExcelSrc = null
        this.content_Changed()
    },
    mounted() {
        this.WIDGET.attrsResize = this.WIDGET.attrsResize || []
        this.WIDGET.attrsResize.push(this.attrResize)
        this.$nextTick(() => this.attrResize())
    },
    beforeUnmount() {
        if (this.blobUrl) { URL.revokeObjectURL(this.blobUrl); this.blobUrl = null }
        this.flexSheet = null
        if (this.WIDGET.attrsResize) {
            const idx = this.WIDGET.attrsResize.indexOf(this.attrResize)
            if (idx !== -1) this.WIDGET.attrsResize.splice(idx, 1)
        }
    },
    methods: {
        content_Changed() {
            this.store.widget_attr_set(this)
            const raw = this.store.attr_get(this.WIDGET, this.attr)
            this._applyRaw(raw)
        },

        _applyRaw(raw) {
            if (this.blobUrl) { URL.revokeObjectURL(this.blobUrl); this.blobUrl = null }
            this.flexSheet = null
            this.pendingExcelSrc = null
            this.excelError = ''
            if (!raw) {
                this.src = ''
                this.fileTitle = ''
                this.resolvedFileType = ''
                return
            }
            let data = raw
            if (typeof raw === 'string') {
                if (raw.trimStart().startsWith('{')) {
                    try { data = JSON.parse(raw) } catch { data = { src: raw } }
                } else {
                    data = { src: raw }
                }
            }
            this.src = data.src || ''
            this.fileTitle = data.title || ''
            this.resolvedFileType = data.fileType || this._mimeFromSrc(this.src)
            if (this.resolvedFileType === 'application/pdf' && this.src.startsWith('data:')) {
                const [header, b64] = this.src.split(',')
                const mime = (header.match(/:(.*?);/) || [])[1] || 'application/pdf'
                const bytes = atob(b64)
                const arr = new Uint8Array(bytes.length)
                for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
                this.blobUrl = URL.createObjectURL(new Blob([arr], { type: mime }))
            }
            if (EXCEL_MIMES.has(this.resolvedFileType) && this.src.startsWith('data:')) {
                this._loadExcel()
            }
        },

        _loadExcel() {
            const src = this.src
            if (this.flexSheet?.hostElement?.isConnected) {
                this._doLoadExcel(src)
            } else {
                this.pendingExcelSrc = src
            }
        },

        async _doLoadExcel(src) {
            this.pendingExcelSrc = null
            try {
                const { Workbook } = await import('@mescius/wijmo.xlsx')
                const wb = new Workbook()
                await new Promise((resolve, reject) => wb.loadAsync(src, resolve, reject))
                if (!this.flexSheet?.hostElement?.isConnected) return
                this.flexSheet.load(wb)
                this.excelError = ''
            } catch (e) {
                this.excelError = t('Failed to read file: {error}', { error: e?.message || String(e) })   // IE 2026 09 23 / ODOO-341407: i18n
            }
        },

        initFlexSheet(flex) {
            flex.isReadOnly = true
            this.flexSheet = flex
            if (this.pendingExcelSrc) {
                this._doLoadExcel(this.pendingExcelSrc)
            }
        },

        _mimeFromSrc(src) {
            if (!src || !src.startsWith('data:')) return ''
            const m = src.match(/^data:([^;,]+)/)
            return m ? m[1] : ''
        },

        attrResize() {
            if (this.attrResize_timeout) clearTimeout(this.attrResize_timeout)
            this.attrResize_timeout = window.$cg.setTimeout(this, 'AttrFileViewer.attrResize', () => {
                this.attrResize_timeout = null
                if (this.attr?.noAutoResize) return
                const wrapEl = this.$refs.wrap
                if (!wrapEl) return
                const wrapRect = wrapEl.getBoundingClientRect()
                if (wrapRect.height <= 10 && !this.src) return
                const elementForm = document.getElementById(`WIDGET${this.widget_id}`)
                if (!elementForm) return
                const widgetAttrsEl = elementForm.querySelector('.widget-attrs')
                const containerRect = (widgetAttrsEl || elementForm).getBoundingClientRect()
                let newHeight = Math.floor(containerRect.bottom - wrapRect.top - 3)
                if (newHeight > containerRect.height) newHeight = 300
                if (newHeight < 80) newHeight = 80
                if (wrapEl.style.height !== `${newHeight}px`) {
                    wrapEl.style.height = `${newHeight}px`
                }
            }, 1)
        },

        openFull() {
            if (!this.src) return
            if (this.viewerType === 'image') {
                const win = window.open()
                win.document.write(`<img src="${this.src}" style="max-width:100%;max-height:100vh;" />`)
            } else {
                window.open(this.src, '_blank')
            }
        },

        download() {
            if (!this.src) return
            const a = document.createElement('a')
            a.href = this.src
            const ext = this._mimeToExt(this.resolvedFileType)
            a.download = this.fileTitle || `file${ext}`
            a.click()
        },

        _mimeToExt(mime) {
            const map = {
                'image/png': '.png', 'image/jpeg': '.jpg', 'image/gif': '.gif',
                'image/webp': '.webp', 'image/svg+xml': '.svg', 'image/bmp': '.bmp',
                'application/pdf': '.pdf',
                'text/plain': '.txt', 'text/csv': '.csv', 'text/html': '.html',
                'application/json': '.json',
                'application/zip': '.zip',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
                'application/vnd.ms-excel': '.xls',
                'application/msword': '.doc',
            }
            return map[mime] || ''
        },
    },
    computed: {
        pdfSrc() {
            return this.blobUrl || this.src
        },

        viewerType() {
            const mime = this.resolvedFileType
            if (!mime) return this.src ? 'image' : 'file'
            if (mime.startsWith('image/')) return 'image'
            if (mime === 'application/pdf') return 'pdf'
            if (EXCEL_MIMES.has(mime)) return 'excel'
            if (mime.startsWith('video/')) return 'video'
            if (mime.startsWith('audio/')) return 'audio'
            if (mime.startsWith('text/') || mime === 'application/json') return 'text'
            return 'file'
        },

        textContent() {
            if (this.viewerType !== 'text' || !this.src) return ''
            try {
                const base64 = this.src.split(',')[1]
                return base64 ? atob(base64) : this.src
            } catch {
                return this.src
            }
        },

        fileIcon() {
            const mime = this.resolvedFileType
            if (mime === 'application/pdf') return 'pi-file-pdf'
            if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('.sheet')) return 'pi-table'
            if (mime.includes('word') || mime.includes('.document') || mime.includes('msword')) return 'pi-file-edit'
            if (mime.includes('presentation') || mime.includes('powerpoint')) return 'pi-desktop'
            if (mime === 'application/zip' || mime.includes('compressed') || mime.includes('archive')) return 'pi-box'
            if (mime.startsWith('audio/')) return 'pi-volume-up'
            if (mime.startsWith('video/')) return 'pi-video'
            if (mime.startsWith('image/')) return 'pi-image'
            return 'pi-file'
        },

        wrapStyle() {
            const h = this.attr?.height
            if (!h) return {}
            return { height: typeof h === 'number' ? h + 'px' : h }
        },
    },
}
</script>

<style scoped>
.attr-fv-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0 4px;
    min-width: 0;
}
.attr-fv-header-icon {
    flex-shrink: 0;
    color: #777;
}
.attr-fv-header-title {
    flex: 1;
    font-size: 13px;
    color: var(--text-color);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}
.attr-fv-btn {
    flex-shrink: 0;
    background: none;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 2px 6px;
    cursor: pointer;
    color: #555;
    line-height: 1.6;
}
.attr-fv-btn:hover { background: #f0f0f0; }

.attr-fv-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 80px;
    overflow: hidden;
}

.attr-fv-empty {
    color: #ccc;
    font-size: 2rem;
}

.attr-fv-img {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    user-select: none;
    cursor: zoom-in;
}

.attr-fv-frame {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
}

.attr-fv-excel {
    width: 100%;
    height: 100%;
    position: relative;
}
.attr-fv-excel-error {
    padding: 8px;
    color: var(--red-500, #e24c4c);
    font-size: 13px;
}
.attr-fv-flex-sheet {
    width: 100%;
    height: 100%;
}

.attr-fv-video {
    max-width: 100%;
    max-height: 100%;
}

.attr-fv-audio {
    width: 100%;
    margin: auto;
}

.attr-fv-text {
    width: 100%;
    height: 100%;
    overflow: auto;
    margin: 0;
    padding: 8px;
    font-size: 12px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-all;
    box-sizing: border-box;
    background: var(--surface-ground, #f8f9fa);
    border-radius: 4px;
}

.attr-fv-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 6px;
    cursor: default;
    width: 100%;
    box-sizing: border-box;
}
.attr-fv-card-icon {
    font-size: 2rem;
    color: #778899;
    flex-shrink: 0;
}
.attr-fv-card-body {
    flex: 1;
    min-width: 0;
}
.attr-fv-card-title {
    font-size: 14px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-color);
}
.attr-fv-card-mime {
    font-size: 11px;
    color: var(--text-color-secondary, #999);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.attr-fv-dl {
    flex-shrink: 0;
    background: none;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 4px 8px;
    cursor: pointer;
    color: #555;
    font-size: 14px;
}
.attr-fv-dl:hover { background: #f0f0f0; }
</style>
